import React from 'react';
import { useDolphin } from '../../hooks/useDolphin';
import { useWallet } from '../../context/WalletContext';
import ConnectPrompt from '../../components/common/ConnectPrompt';
import toast from 'react-hot-toast';
import { FaGift, FaLock, FaClock, FaCoins } from 'react-icons/fa';

const Claim = () => {
  const { isConnected } = useWallet();
  const { stats, userStats, claimTokens, txLoading } = useDolphin();

  if (!isConnected && localStorage.getItem('walletPromptSkipped') !== 'true') {
    return <ConnectPrompt />;
  }

  const calculateClaimable = () => {
    if (!stats.miningEnded || stats.miningEndTimestamp === 0) {
      return 0;
    }

    const now = Math.floor(Date.now() / 1000);
    const daysPassed = Math.floor((now - stats.miningEndTimestamp) / 86400);
    const effectiveDays = Math.min(daysPassed, stats.vestingDays);
    
    const totalVested = parseFloat(userStats.vested);
    const claimable = (totalVested * effectiveDays) / stats.vestingDays;
    
    return claimable;
  };

  const getDaysRemaining = () => {
    if (!stats.miningEnded || stats.miningEndTimestamp === 0) {
      return stats.vestingDays;
    }

    const now = Math.floor(Date.now() / 1000);
    const daysPassed = Math.floor((now - stats.miningEndTimestamp) / 86400);
    return Math.max(0, stats.vestingDays - daysPassed);
  };

  const getVestingProgress = () => {
    if (!stats.miningEnded || stats.miningEndTimestamp === 0) {
      return 0;
    }

    const now = Math.floor(Date.now() / 1000);
    const daysPassed = Math.floor((now - stats.miningEndTimestamp) / 86400);
    return Math.min(100, (daysPassed / stats.vestingDays) * 100);
  };

  const handleClaim = async () => {
    if (localStorage.getItem('walletPromptSkipped') === 'true') return null;
    try {
      await claimTokens();
      toast.success('Tokens claimed successfully!');
    } catch (err) {
      console.error('Claim error:', err);
      toast.error(err.message || 'Failed to claim tokens');
    }
  };

  const claimableAmount = calculateClaimable();
  const daysRemaining = getDaysRemaining();
  const vestingProgress = getVestingProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white p-1 sm:p-2 md:p-3 lg:p-4 overflow-x-hidden">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 border border-gray-700 shadow-xl w-full max-w-2xl">
        {!stats.miningEnded ? (
          <div className="w-full p-4 bg-gradient-to-r from-yellow-700/80 to-yellow-800/80 border border-yellow-600 rounded-xl text-yellow-200 font-semibold text-left mb-2">
            <FaLock className="inline-block mr-2 mb-1 text-yellow-300" />
            Mining is still ongoing. Claiming will be available after mining ends.
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-2 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-lg">
                <FaGift className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold truncate">Claim Tokens</h1>
                <p className="text-xs sm:text-sm text-gray-400 truncate">Withdraw your vested DOLP</p>
              </div>
            </div>
            {/* Stat Cards */}
            <div className="flex flex-col sm:flex-row gap-3 w-full mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FaCoins className="w-3 h-3 text-emerald-400" />
                  <span className="text-xs font-bold truncate">Total Vested</span>
                </div>
                <div className="text-base font-bold text-emerald-200 text-left">{Number(userStats.vested).toLocaleString()} <span className="text-xs font-normal text-emerald-500">DOLP</span></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FaGift className="w-3 h-3 text-cyan-400" />
                  <span className="text-xs font-bold truncate">Claimable</span>
                </div>
                <div className="text-base font-bold text-cyan-200 text-left">{claimableAmount.toLocaleString(undefined, { maximumFractionDigits: 4 })} <span className="text-xs font-normal text-cyan-500">DOLP</span></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FaClock className="w-3 h-3 text-yellow-400" />
                  <span className="text-xs font-bold truncate">Days Left</span>
                </div>
                <div className="text-base font-bold text-yellow-200 text-left">{daysRemaining} <span className="text-xs font-normal text-yellow-500">days</span></div>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="w-full mb-4">
              <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 via-emerald-400 to-yellow-300 rounded-full shadow-lg" style={{ width: `${vestingProgress}%` }} />
              </div>
              <div className="flex text-[11px] text-gray-400 mt-1">
                <span className="text-cyan-400 font-semibold text-left flex-1">Mining Ended</span>
                <span className="text-emerald-300 font-semibold text-center flex-1">{vestingProgress.toFixed(1)}% Vested</span>
                <span className="text-yellow-300 font-semibold text-right flex-1">Fully Vested</span>
              </div>
            </div>
            {/* Claim Button */}
            <button
              className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-bold text-base shadow-lg hover:from-emerald-700 hover:to-cyan-700 transition disabled:opacity-60 flex items-center gap-2 justify-start"
              onClick={handleClaim}
              disabled={txLoading || claimableAmount <= 0}
            >
              {txLoading ? (
                <span className="flex items-center gap-2"><span className="spinner w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>Claiming...</span>
              ) : (
                <><FaGift className="text-lg" />Claim {claimableAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} DOLP</>
              )}
            </button>
            <div className="w-full text-[12px] text-gray-400 mt-3 text-left">
              Tokens unlock linearly over the vesting period. After full vesting, 100% of your tokens are claimable.
            </div>
          </>
        )}
      </div>
    </div>

  );
}
export default Claim;
